import React, { ReactNode, useEffect, useState } from "react";
import "./CloseoutFormFields.css";
import { useDispatch, useSelector } from "react-redux";
import urls from "../../configmap/config.json";
import { documentclassification } from "../../utils/fieldConstants";
import { transformProjectDetails } from "../../utils/transformProjectDetails";
import { AccordionItemV2, AccordionV2 } from "pepsico-ds";
import CloseOutFields from "./CloseOutFields/CloseOutFields";
import IntakeFields from "./IntakeFields/IntakeFields";
import { CloseOutActions } from "../../redux/closeoutslice/closeoutSlice";

interface CloseoutFieldsProps {
  formData: {
    [key: string]: string | number | Date | FileList | null;
  };
  handleChange: (value: any, key: string) => void;
  // handleFileChange: (files: FileList | null) => void;
  handleFileChange?: (files: File[]) => void;
  handleClose: () => void;
  inputValue?: any;
  formattedProjectId?: any;
  closeoutErrors: { [key: string]: string | null };
  inputError: { [key: string]: string | null };
  closeoutOnFocus: (key: string) => void; // Add focus handler
  closeoutOnBlur: (key: string) => void; // Add blur handler
  // projectData : any;
  mode: any;
  children?: ReactNode;
}

interface ProjectFile {
  file_id: number;
  project_id: number;
  blob_file_name: string;
  file_name: string;
  document_classification: number;
  user_id: string;
  is_file_type_converted: number;
  is_vector_db_processed: number;
  status: boolean;
  meta_data: any;
  file_size: number;
  file_type: any;
  created_date: string | null;
}

const CloseoutFormFields: React.FC<CloseoutFieldsProps> = ({
  formData,
  handleChange,
  closeoutErrors,
  closeoutOnFocus,
  closeoutOnBlur,
  inputError,
  // projectData,
  mode,
  children,
}) => {
  const dispatch = useDispatch();
  const [fileClassifications, setFileClassifications] = useState<
    Record<string, string>
  >({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [exceededFiles, setExceededFiles] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileToRemove, setFileToRemove] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  const [localProjectData, setLocalProjectData] = useState<any>({}); // Transformed project data
  const maxFileSizeInMB = urls.maxFileSize / (1024 * 1024);

  const projectDetails = useSelector(
    (state: any) => state.project.projectDetails
  );

  const {
    files,
    uploadedFilesSuccess,
    internalSelection,
    restrictedSelection,
    confidentialSelection,
  } = useSelector((state: any) => state.closeOut);

  useEffect(() => {
    const newlyUploadedFiles = files.filter(
      (file: any) => !uploadedFilesSuccess.includes(file.name)
    );
    const filesToCompare =
      newlyUploadedFiles.length > 0 ? newlyUploadedFiles : files;
    // Check if all Confidential radio buttons are selected
    const allConfidentialSelected = filesToCompare.every(
      (file: any) =>
        confidentialSelection[file.name] === "yes" ||
        file.document_classification === documentclassification.confidential
    );

    dispatch(CloseOutActions.setConfidentialChecked(allConfidentialSelected));

    // Check if all Restricted radio buttons are selected
    const allRestrictedSelected = filesToCompare.every(
      (file: any) =>
        restrictedSelection[file.name] === "yes" ||
        file.document_classification === documentclassification.restricted
    );
    dispatch(CloseOutActions.setRestrictedChecked(allRestrictedSelected));

    //Check if all Internal radio buttons are selected
    const allInternalSelected = filesToCompare.every(
      (file: any) =>
        internalSelection[file.name] === "yes" ||
        file.document_classification === documentclassification.internal
    );
    dispatch(CloseOutActions.setInternalChecked(allInternalSelected));
  }, [
    dispatch,
    confidentialSelection,
    restrictedSelection,
    internalSelection,
    files,
    uploadedFilesSuccess,
  ]);

  useEffect(() => {
    if (projectDetails && projectDetails.code === 200) {
      var { projectData, files } = transformProjectDetails(projectDetails);
      setLocalProjectData(projectData);

      // This will hold the current list of files that we will display and work with
      const allFiles = [...files];
      const now = Date.now();
      // Transform API file data to match the format you're using
      const transformedFiles: any = allFiles.map((apiFile) => {
        // Check if the file is already uploaded (by checking its status)
        const isUploaded = apiFile.status;

        // Ensure we create a valid `File` object for the upload logic
        let fileObject;

        if (isUploaded) {
          // For previously uploaded files, simulate a `File` object using metadata
          fileObject = new File([], apiFile.file_name, {
            type: apiFile.file_type || "application/octet-stream", // Default MIME type
            lastModified: now, // Simulate the last modified timestamp
          });
        } else {
          // For new files, simulate a `File` object or fetch actual file data
          const blob = new Blob([], {
            type: apiFile.file_type || "application/octet-stream",
          }); // Empty blob (or use actual file data if available)
          fileObject = new File([blob], apiFile.file_name, {
            type: apiFile.file_type || "application/octet-stream",
            lastModified: now,
          });
        }
        return Object.freeze({
          file: fileObject, // The actual File object
          name: fileObject.name, // File name
          blob_file_name: apiFile.blob_file_name, // Blob file name from API
          size: fileObject.size, // File size
          type: fileObject.type, // File type
          lastModified: now, // Use current timestamp for lastModified
          document_classification: apiFile.document_classification,
          lastModifiedDate: now, // Use current date for lastModifiedDate
          webkitRelativePath: "", // Not relevant in this case
        });
      });

      // Merge transformed API files with existing files in state
      dispatch(CloseOutActions.addFiles(transformedFiles));

      // Mark files as successfully uploaded based on `status` from API data
      const uploadedFileNames = files
        .filter((file) => file.status) // If file's `status` is true, it has been uploaded
        .map((file) => file.file_name); // Get the names of uploaded files

      // Update state with successfully uploaded files
      // setUploadedFilesSuccess((prev) => [...prev, ...uploadedFileNames]);
      dispatch(CloseOutActions.setUploadedfileSuccess(uploadedFileNames));
    }
  }, [projectDetails]);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div className="form-container">
            <AccordionV2
              presentation="box-shadow"
              selectedItemIndex={1}
              className="peacockAccordian"
            >
              <AccordionItemV2 label="1. Intake" presentation="box-shadow">
                <IntakeFields mode={mode} localProjectData={localProjectData} />
              </AccordionItemV2>
              <AccordionItemV2 label="2. Close Out" presentation="box-shadow">
                <>
                  <CloseOutFields
                    formData={formData}
                    mode={mode}
                    closeoutErrors={closeoutErrors}
                    handleChange={handleChange}
                    closeoutOnFocus={closeoutOnFocus}
                    closeoutInputErrors={inputError}
                    closeoutOnBlur={closeoutOnBlur}
                    localProjectData={localProjectData}
                  />
                  {children}
                </>
              </AccordionItemV2>
            </AccordionV2>
          </div>
        </div>
      </div>
    </>
  );
};

export default CloseoutFormFields;
